interface TokenAvatarProps {
  imageUrl?: string;
  symbol?: string;
  /**  
   * sm / md / lg give you explicit sizes.  
   * omit size (or pass "auto") to have the avatar fill the cell completely.  
   */
  size?: 'sm' | 'md' | 'lg' | 'auto';
  className?: string;
}

/**  
 *  TokenAvatar  –  shows the project image or a circular initial.  
 *  Uses `aspect-square` so the circle never stretches.  
 */
export default function TokenAvatar({
  imageUrl,
  symbol,
  size = 'auto',
  className = '',
}: TokenAvatarProps) {
  /* -- Sizes -------------------------------------------------------------- */
  const sizeClasses: Record<NonNullable<TokenAvatarProps['size']>, string> = {
    sm:   'w-16 aspect-square',
    md:   'w-20 aspect-square',
    lg:   'w-24 aspect-square',
    auto: 'w-full aspect-square',      // fills the whole grid cell
  };

  const textSizeClasses: Record<NonNullable<TokenAvatarProps['size']>, string> =
    /** make the letter roughly half the circle’s diameter */
    {
      sm:   'text-3xl font-bold',
      md:   'text-4xl font-bold',
      lg:   'text-5xl font-bold',
      auto: 'text-5xl lg:text-6xl font-bold',
    };

  /* -- Helpers ------------------------------------------------------------ */
  const getFallbackLetter = (token?: string) =>
    token?.toUpperCase() === 'AI16Z'
      ? 'A'
      : token?.[0]?.toUpperCase() ?? '?';

  const fallbackLetter = getFallbackLetter(symbol);
  const hasValidImage =
    !!imageUrl && imageUrl.trim() !== '' && imageUrl !== 'null' && imageUrl !== 'undefined';

  /* -- Fallback circle (always perfectly round) --------------------------- */
  const Fallback = () => (
    <div
      className={`${sizeClasses[size]} rounded-full bg-gradient-to-br from-blue-500 to-purple-600
                  flex items-center justify-center border-2 border-blue-300 shadow-lg`}
    >
      <span className={`${textSizeClasses[size]} text-white leading-none`}>
        {fallbackLetter}
      </span>
    </div>
  );

  /* -- Render ------------------------------------------------------------- */
  return (
    <div className={`${sizeClasses[size]} relative ${className}`}>
      {hasValidImage ? (
        <img
          src={imageUrl}
          alt={`${symbol ?? 'Token'} logo`}
          className={`w-full h-full rounded-full object-cover`}
          onError={(e) => {
            /* swap the broken img for fallback without React state juggling */
            const container = e.currentTarget.parentElement;
            if (container) container.innerHTML = container.innerHTML; // force a re-render
          }}
        />
      ) : (
        <Fallback />
      )}
    </div>
  );
}